/**
 * localStorage access for saved wheel slots.
 *
 * Lives here rather than in SavedWheelsModal so non-modal callers (the HomePage
 * badge count) don't have to import from a component. Every read tolerates
 * absent/corrupt payloads and every write reports quota failure instead of
 * throwing, because a single bad value would otherwise break the feature with
 * no way to recover from the UI.
 */
import { MAX_SLOT_HISTORY, type SavedWheel } from '../types/wheel';
import { randomFloat } from './random';
import { normalizeEntries, normalizeTitle } from './wheelData';

export const SAVED_WHEELS_KEY = 'npw_saved_wheels';
export const MAX_SAVED_WHEELS = 20;

/** Read the saved list, tolerating absent, corrupt, or non-array payloads. */
export function readSavedWheels(): SavedWheel[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SAVED_WHEELS_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, MAX_SAVED_WHEELS).flatMap(raw => {
      if (!raw || typeof raw !== 'object') return [];
      const value = raw as Record<string, unknown>;
      if (typeof value.id !== 'string' || typeof value.name !== 'string') return [];
      const entries = normalizeEntries(value.entries);
      if (!entries?.length) return [];

      const history = Array.isArray(value.history)
        ? value.history
          .filter(item => item && typeof item === 'object')
          .flatMap(item => {
            const row = item as Record<string, unknown>;
            return typeof row.name === 'string' && typeof row.time === 'string' && typeof row.date === 'string'
              ? [{ name: row.name.slice(0, 200), time: row.time.slice(0, 40), date: row.date.slice(0, 10) }]
              : [];
          })
          .slice(-MAX_SLOT_HISTORY)
        : undefined;

      const wheel: SavedWheel = {
        id: value.id.slice(0, 100),
        name: value.name.trim().slice(0, 60) || 'Saved wheel',
        title: normalizeTitle(value.title),
        entries,
        entryCount: entries.length,
        savedAt: typeof value.savedAt === 'string' ? value.savedAt : '',
      };
      if (typeof value.updatedAt === 'string') wheel.updatedAt = value.updatedAt;
      if (value.options && typeof value.options === 'object' && !Array.isArray(value.options)) {
        wheel.options = value.options as SavedWheel['options'];
      }
      if (history) wheel.history = history;
      return [wheel];
    });
  } catch {
    return [];
  }
}

export function writeSavedWheels(wheels: SavedWheel[]): boolean {
  try {
    localStorage.setItem(SAVED_WHEELS_KEY, JSON.stringify(wheels));
    return true;
  } catch {
    return false; // storage full or blocked
  }
}

function newId(): string {
  // crypto.randomUUID needs a secure context; fall back for http:// and older browsers.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return randomFloat().toString(36).slice(2) + Date.now().toString(36);
}

export type SaveFailure = 'limit' | 'storage';

export type AddResult =
  | { ok: true; wheels: SavedWheel[]; wheel: SavedWheel }
  | { ok: false; wheels: SavedWheel[]; reason: SaveFailure };

/** Prepend a new slot, respecting MAX_SAVED_WHEELS. */
export function addSavedWheel(input: Omit<SavedWheel, 'id' | 'savedAt'>): AddResult {
  const existing = readSavedWheels();
  if (existing.length >= MAX_SAVED_WHEELS) {
    return { ok: false, wheels: existing, reason: 'limit' };
  }
  const wheel: SavedWheel = { ...input, id: newId(), savedAt: new Date().toISOString() };
  const wheels = [wheel, ...existing];
  if (!writeSavedWheels(wheels)) {
    return { ok: false, wheels: existing, reason: 'storage' };
  }
  return { ok: true, wheels, wheel };
}

export type OverwriteResult =
  | { ok: true; wheels: SavedWheel[] }
  | { ok: false; wheels: SavedWheel[]; reason: SaveFailure };

/** Replace the contents of an existing slot, stamping updatedAt. */
export function overwriteSavedWheel(
  id: string,
  patch: Partial<Pick<SavedWheel, 'title' | 'entries' | 'entryCount' | 'options'>>,
): OverwriteResult {
  const existing = readSavedWheels();
  const wheels = existing.map(w =>
    w.id === id ? { ...w, ...patch, updatedAt: new Date().toISOString() } : w
  );
  if (!writeSavedWheels(wheels)) {
    return { ok: false, wheels: existing, reason: 'storage' };
  }
  return { ok: true, wheels };
}

export function deleteSavedWheel(id: string): SavedWheel[] {
  const wheels = readSavedWheels().filter(w => w.id !== id);
  writeSavedWheels(wheels);
  return wheels;
}

/** Rename a slot without touching its entries or history. */
export function renameSavedWheel(id: string, name: string): SavedWheel[] {
  const trimmed = name.trim();
  if (!trimmed) return readSavedWheels();
  const wheels = readSavedWheels().map(w =>
    w.id === id ? { ...w, name: trimmed.slice(0, 60), updatedAt: new Date().toISOString() } : w
  );
  writeSavedWheels(wheels);
  return wheels;
}

/**
 * Append one spin to a slot's own history.
 *
 * Called on every spin while that slot is loaded, so the record builds up as
 * the class runs rather than only when someone remembers to press Save. Writes
 * are best-effort: a full quota must not break the spin itself.
 */
export function appendSlotHistory(id: string, entry: { name: string; time: string }): void {
  const wheels = readSavedWheels();
  const i = wheels.findIndex(w => w.id === id);
  if (i === -1) return;
  const history = [
    ...(wheels[i].history ?? []),
    { ...entry, date: new Date().toISOString().slice(0, 10) },
  ].slice(-MAX_SLOT_HISTORY);
  wheels[i] = { ...wheels[i], history };
  writeSavedWheels(wheels);
}

export function clearSlotHistory(id: string): SavedWheel[] {
  const wheels = readSavedWheels().map(w => (w.id === id ? { ...w, history: [] } : w));
  writeSavedWheels(wheels);
  return wheels;
}

/**
 * How often each entry has come up, most-picked first.
 *
 * The number a teacher actually wants: not "who won" but "who have I not called
 * on yet". Names still on the wheel but absent from the history are included
 * with a count of zero — those are the ones being missed.
 */
export function pickCounts(wheel: SavedWheel): { name: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const e of wheel.entries) counts.set(e.name, 0);
  for (const h of wheel.history ?? []) counts.set(h.name, (counts.get(h.name) ?? 0) + 1);
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}
